import type { CodigoGrupo, Parametros } from '@precificador/shared/pricing.js';
import { api, baixar, enviarArquivo } from './cliente';
import type {
  AmbienteAtivo,
  Condicoes,
  Conexao,
  DadosConexao,
  HistoricoCusto,
  LinhaImportacao,
  MaterialCusto,
  Mensagem,
  ModoEscrita,
  ParametrosPorGrupo,
  Perfil,
  Preparacao,
  ResultadoPublicacao,
  ResultadoTesteConexao,
  ResumoListaRodada,
  RodadaCompleta,
  Sessao,
  StatusRodada,
  TabelaPreco,
  Usuario,
} from './tipos';

export const auth = {
  me: () => api<Sessao>('/auth/me'),
  entrar: (login: string, senha: string) => api<Sessao>('/auth/login', { metodo: 'POST', corpo: { login, senha } }),
  sair: () => api<void>('/auth/logout', { metodo: 'POST' }),
  trocarSenha: (senhaAtual: string, novaSenha: string) =>
    api<Sessao>('/auth/trocar-senha', { metodo: 'POST', corpo: { senhaAtual, novaSenha } }),
};

export const usuarios = {
  listar: () => api<{ usuarios: Usuario[] }>('/admin/usuarios'),
  criar: (dados: { nome: string; login: string; perfil: Perfil; senhaTemporaria: string }) =>
    api<{ usuario: Usuario }>('/admin/usuarios', { metodo: 'POST', corpo: dados }),
  editar: (id: number, dados: { nome?: string; perfil?: Perfil; ativo?: boolean }) =>
    api<{ usuario: Usuario }>(`/admin/usuarios/${id}`, { metodo: 'PATCH', corpo: dados }),
  redefinirSenha: (id: number, senhaTemporaria: string) =>
    api<{ usuario: Usuario }>(`/admin/usuarios/${id}/redefinir-senha`, { metodo: 'POST', corpo: { senhaTemporaria } }),
};

export const conexoes = {
  listar: () => api<{ conexoes: Conexao[]; ativo: AmbienteAtivo | null; modoEscrita: ModoEscrita }>('/admin/conexoes'),
  criar: (dados: DadosConexao) => api<{ conexao: Conexao }>('/admin/conexoes', { metodo: 'POST', corpo: dados }),
  salvar: (id: number, dados: DadosConexao) => api<{ conexao: Conexao }>(`/admin/conexoes/${id}`, { metodo: 'PUT', corpo: dados }),
  ativar: (id: number) => api<{ ativo: AmbienteAtivo | null }>(`/admin/conexoes/${id}/ativar`, { metodo: 'POST' }),
  excluir: (id: number) => api<void>(`/admin/conexoes/${id}`, { metodo: 'DELETE' }),
  testar: (dados: DadosConexao & { id?: number }) => api<ResultadoTesteConexao>('/admin/conexoes/testar', { metodo: 'POST', corpo: dados }),
};

export const mensagens = {
  listar: () => api<{ mensagens: Mensagem[] }>('/admin/mensagens'),
  criar: (texto: string) => api<{ mensagens: Mensagem[] }>('/admin/mensagens', { metodo: 'POST', corpo: { texto } }),
  editar: (id: number, dados: { texto?: string; ativo?: boolean }) =>
    api<{ mensagens: Mensagem[] }>(`/admin/mensagens/${id}`, { metodo: 'PATCH', corpo: dados }),
  ordenar: (ids: number[]) => api<{ mensagens: Mensagem[] }>('/admin/mensagens/ordem', { metodo: 'PUT', corpo: { ids } }),
};

export const custos = {
  listar: () => api<{ materiais: MaterialCusto[] }>('/custos'),
  salvar: (codigo: string, custoKg: number | null, observacao?: string | null) =>
    api<{ custo: Pick<MaterialCusto, 'cdMaterial' | 'custoKg' | 'observacao' | 'atualizadoEm' | 'atualizadoPor'> }>(
      `/custos/${encodeURIComponent(codigo)}`,
      { metodo: 'PUT', corpo: { custoKg, observacao } },
    ),
  salvarAcrescimo: (codigo: string, acrescimoUnit: number | null) =>
    api<{ acrescimo: { cdMaterial: string; acrescimoUnit: number | null } }>(`/custos/${encodeURIComponent(codigo)}/acrescimo`, {
      metodo: 'PUT',
      corpo: { acrescimoUnit },
    }),
  acrescimoLote: (codigos: string[], acrescimoUnit: number | null) =>
    api<{ alterados: number; semAlteracao: number }>('/custos/acrescimo-lote', { metodo: 'PUT', corpo: { codigos, acrescimoUnit } }),
  historico: (codigo: string) => api<{ historico: HistoricoCusto[] }>(`/custos/${encodeURIComponent(codigo)}/historico`),
  previaImportacao: (arquivo: File) => enviarArquivo<{ arquivo: string; temAcrescimo: boolean; itens: LinhaImportacao[] }>('/custos/importar/previa', arquivo),
  confirmarImportacao: (itens: { codigo: string; custoKg: number; acrescimoUnit?: number | null }[]) =>
    api<{ gravados: number; semAlteracao: number }>('/custos/importar/confirmar', { metodo: 'POST', corpo: { itens } }),
};

export const tabelas = {
  listar: () => api<{ tabelas: TabelaPreco[] }>('/tabelas'),
  preparacao: (codigo: string) => api<Preparacao>(`/tabelas/${encodeURIComponent(codigo)}/preparacao`),
  condicoes: (codigo: string) => api<{ condicoes: Condicoes }>(`/tabelas/${encodeURIComponent(codigo)}/condicoes`),
  salvarCondicoes: (codigo: string, condicoes: Condicoes) =>
    api<{ condicoes: Condicoes }>(`/tabelas/${encodeURIComponent(codigo)}/condicoes`, { metodo: 'PUT', corpo: condicoes }),
  baixarPdf: (codigo: string) => baixar(`/tabelas/${encodeURIComponent(codigo)}/pdf`, `Tabela_${codigo}.pdf`),
};

export const rodadas = {
  listar: (filtro: { tbPreco?: string; status?: StatusRodada } = {}) => {
    const q = new URLSearchParams();
    if (filtro.tbPreco) q.set('tbPreco', filtro.tbPreco);
    if (filtro.status) q.set('status', filtro.status);
    return api<{ rodadas: ResumoListaRodada[] }>(`/rodadas?${q.toString()}`);
  },
  criar: (dados: { tbPreco: string; parametros: ParametrosPorGrupo; condicoes: Condicoes; travarPreco: boolean }) =>
    api<RodadaCompleta>('/rodadas', { metodo: 'POST', corpo: dados }),
  obter: (id: number) => api<RodadaCompleta>(`/rodadas/${id}`),
  editarItem: (
    id: number,
    itemId: number,
    edicao: { margemPct?: number | null; negociacaoPct?: number | null; precoUnit?: number | null; liberarPreco?: boolean },
  ) =>
    api<RodadaCompleta>(`/rodadas/${id}/itens/${itemId}`, { metodo: 'PATCH', corpo: edicao }),
  atualizarParametros: (id: number, parametros: Partial<Record<CodigoGrupo, Parametros>>) =>
    api<RodadaCompleta & { preservados: number }>(`/rodadas/${id}/parametros`, { metodo: 'PUT', corpo: { parametros } }),
  definirTrava: (id: number, travarPreco: boolean) => api<RodadaCompleta>(`/rodadas/${id}/trava`, { metodo: 'PUT', corpo: { travarPreco } }),
  recarregarCustos: (id: number) => api<RodadaCompleta & { custosAlterados: number }>(`/rodadas/${id}/recarregar-custos`, { metodo: 'POST' }),
  descartar: (id: number) => api<RodadaCompleta>(`/rodadas/${id}/descartar`, { metodo: 'POST' }),
  publicar: (id: number, confirmacao: string) =>
    api<RodadaCompleta & { resultado: ResultadoPublicacao }>(`/rodadas/${id}/publicar`, { metodo: 'POST', corpo: { confirmacao } }),
  baixarPdf: (id: number) => baixar(`/rodadas/${id}/pdf`, `Previa_${id}.pdf`),
  baixarXlsx: (id: number) => baixar(`/rodadas/${id}/xlsx`, `Rodada_${id}.xlsx`),
};
